import { useState, useEffect } from "react";

interface ResponsiveChartHeightOptions {
    heightPercent?: number;
    widthPercent?: number;
    minHeight?: number;
    maxHeight?: number;
    config?: {
        chartDimensions?: {
            heightPercent?: number;
            widthPercent?: number;
            minHeight?: number;
            maxHeight?: number;
        };
    };
}

/**
 * Custom hook to calculate responsive chart height based on viewport and config.
 *
 * @param options.heightPercent - Percentage of viewport height (default 0.25)
 * @param options.widthPercent - Percentage of viewport width (default 0)
 * @param options.minHeight - Minimum height in px (default 250)
 * @param options.maxHeight - Maximum height in px (default 500)
 * @param options.config - Optional config object with chartDimensions overrides
 * @returns chartHeight as a string (e.g., '300px')
 */
const useResponsiveChartHeight = (options: ResponsiveChartHeightOptions = {}) => {
    const {
        config,
        heightPercent: propHeightPercent,
        widthPercent: propWidthPercent,
        minHeight: propMinHeight,
        maxHeight: propMaxHeight,
    } = options;

    const chartDims = config?.chartDimensions || {};
    const heightPercent = propHeightPercent ?? chartDims.heightPercent ?? 0.25;
    const widthPercent = propWidthPercent ?? chartDims.widthPercent ?? 0;
    const minHeight = propMinHeight ?? chartDims.minHeight ?? 250;
    const maxHeight = propMaxHeight ?? chartDims.maxHeight ?? 500;

    const [chartHeight, setChartHeight] = useState<string>(
        `${Math.max(minHeight, Math.min(Math.floor(window.innerHeight * heightPercent + window.innerWidth * widthPercent), maxHeight))}px`
    );

    useEffect(() => {
        const updateHeight = () => {
            const vh = window.innerHeight;
            const vw = window.innerWidth;
            const calculated = Math.floor(vh * heightPercent + vw * widthPercent);
            setChartHeight(`${Math.max(minHeight, Math.min(calculated, maxHeight))}px`);
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [heightPercent, widthPercent, minHeight, maxHeight]);

    return chartHeight;
}

export default useResponsiveChartHeight;